import React, { useMemo, useState } from "react";
import { OVERVIEW, CHANNEL_BADGE, CHANNEL_HEAD, advertiserRowClass } from "../../../../../core/theme/reportTheme";
import { BUSINESS_TYPE_LABELS, BASIS_LABELS } from "../../../../../core/data/businessTypes/labels";
import { VISIBILITY_DESCRIPTION, VISIBILITY_CHANNELS, CHANNEL_NAMES, providerError } from "../../../../../core/data/marketOverviewText";
import { sortAdvertisers, nextSort } from "../../../../../core/data/sortAdvertisers";
import SortButton from "../../../components/SortButton";
import { useTypeMenu } from "../../../components/TypeMenu";

const CHANNEL_COLUMNS = ["ppc", "local", "seo", "shopping", "lsa"];
const DEFAULT_SORT = { column: "visibility", dir: "desc" };

// "Who shows up most": every advertiser, ranked by visibility.
// A channel column is hidden when nobody appeared in it.
// Every column heading sorts; the table always opens ranked by visibility.
// Clicking a row opens Advertiser Overview pinned to that advertiser.
// highlightKeys: advertisers highlighted in yellow (from the Advertisers filter).
export default function AdvertiserTable({ result, onSetType, onOpenAdvertiser, highlightKeys = [], expandButton }) {
  const [sort, setSort] = useState(DEFAULT_SORT);
  const { openMenu, menu } = useTypeMenu(onSetType);
  const channels = CHANNEL_COLUMNS.filter(c => result.totals[c] > 0);
  const hiddenChannels = CHANNEL_COLUMNS.filter(c => result.totals[c] === 0);
  const showDevices = result.sampling.devices.length > 0;
  const count = result.advertisers.length;
  // "3 of 5": out of the keywords and postcodes with completed searches in these filters.
  const keywordsSearched = result.gapSummary.keywordsBySearches.filter(k => k.searches > 0).length;
  const postcodesSearched = result.gapSummary.postcodes;

  // A filter can hide the sorted column; fall back to visibility then.
  const sortChannel = sort.column.split("_")[0];
  const activeSort = CHANNEL_COLUMNS.includes(sortChannel) && !channels.includes(sortChannel) ? DEFAULT_SORT : sort;
  const isDefaultSort = activeSort.column === DEFAULT_SORT.column && activeSort.dir === DEFAULT_SORT.dir;
  const rows = useMemo(() => sortAdvertisers(result.advertisers, activeSort), [result.advertisers, activeSort]);
  const sortProps = { sort: activeSort, onSort: column => setSort(nextSort(activeSort, column)) };

  return (
    <div className={`${OVERVIEW.panel} flex-1 min-w-0`}>
      <div className={OVERVIEW.panelHeader}>
        <div className={OVERVIEW.panelTitleRow}>
          <h3 className={OVERVIEW.panelTitle}>Who shows up most</h3>
          <span className={OVERVIEW.panelNote}>
            {count} advertiser{count === 1 ? "" : "s"} · ranked by visibility ({VISIBILITY_DESCRIPTION}) · counted by website
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {!isDefaultSort && (
            <button type="button" onClick={() => setSort(DEFAULT_SORT)} className={OVERVIEW.link}>
              Back to visibility ranking
            </button>
          )}
          {expandButton}
        </div>
      </div>
      <div className={OVERVIEW.panelScroll}>
        {count === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400 text-center">{providerError(result) ? `${providerError(result).label}.` : "No advertisers seen for these filters."}</p>
        ) : (
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th rowSpan={2} className={OVERVIEW.thFirst}><SortButton column="rank" label="#" {...sortProps} /></th>
                <th rowSpan={2} className={OVERVIEW.th}><SortButton column="advertiser" label="Advertiser" {...sortProps} /></th>
                <th rowSpan={2} className={OVERVIEW.th}><SortButton column="type" label="Type" {...sortProps} /></th>
                <th rowSpan={2} className={OVERVIEW.th} title={`Visibility: ${VISIBILITY_DESCRIPTION}`}><SortButton column="visibility" label="Visibility" {...sortProps} /></th>
                {channels.map(c => (
                  <th key={c} colSpan={2} className={`${OVERVIEW.thGroup} ${CHANNEL_HEAD[c]}`}>{CHANNEL_NAMES[c]}</th>
                ))}
                {showDevices && <th rowSpan={2} className={OVERVIEW.thCenter} title={`Searches seen in ${VISIBILITY_CHANNELS}, on mobile`}><SortButton column="mobile" label="Mobile" {...sortProps} /></th>}
                {showDevices && <th rowSpan={2} className={OVERVIEW.thCenter} title={`Searches seen in ${VISIBILITY_CHANNELS}, on desktop`}><SortButton column="desktop" label="Desktop" {...sortProps} /></th>}
                <th rowSpan={2} className={OVERVIEW.thCenter} title="Keywords they were seen for"><SortButton column="keywords" label="Keywords" {...sortProps} /></th>
                <th rowSpan={2} className={OVERVIEW.thCenter} title="Postcodes they were seen in"><SortButton column="locations" label="Locations" {...sortProps} /></th>
              </tr>
              <tr>
                {channels.map(c => (
                  <React.Fragment key={c}>
                    <th className={OVERVIEW.thSub} title={`Searches they were seen in (${CHANNEL_NAMES[c]})`}><SortButton column={`${c}_seen`} label="Seen" {...sortProps} /></th>
                    <th className={OVERVIEW.thSub} title="Average position when seen (1 = top)"><SortButton column={`${c}_pos`} label="Avg pos" {...sortProps} /></th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(a => {
                const look = advertiserRowClass(false, highlightKeys.includes(a.key));
                return (
                <tr
                  key={a.key}
                  className={`${look.row} ${onOpenAdvertiser ? "cursor-pointer" : ""}`}
                  onClick={onOpenAdvertiser ? () => onOpenAdvertiser(a.key) : undefined}
                  title={onOpenAdvertiser ? `Open Advertiser Snapshot for ${a.displayName}` : undefined}
                >
                  <td className={`${OVERVIEW.tdFirst} ${look.first}`}>{a.rank}</td>
                  <td className={`${OVERVIEW.td} max-w-[14.375rem]`}>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="truncate text-slate-900" title={a.displayName}>{a.displayName}</span>
                      {a.matchedBy === "name" && <span className={OVERVIEW.tag} title="This listing has no website, so it is matched by its name.">No website</span>}
                      {a.matchedBy === "likely" && <span className={OVERVIEW.tag} title={`Matched by name - worth a check: ${a.likelyNames.join(" · ")}`}>likely match</span>}
                    </div>
                  </td>
                  <td className={OVERVIEW.td}>
                    <button
                      type="button"
                      onClick={e => openMenu(a, e)}
                      className="flex items-center gap-1.5 hover:underline underline-offset-2"
                      title={`Classified as ${BUSINESS_TYPE_LABELS[a.businessType.type]} (${BASIS_LABELS[a.businessType.basis]}). Click to correct.`}
                    >
                      <span>{BUSINESS_TYPE_LABELS[a.businessType.type]}</span>
                      {a.businessType.basis === "manual" && <span className={OVERVIEW.muted}>✎</span>}
                    </button>
                  </td>
                  <td className={OVERVIEW.td} title={`${a.visibility.score}${VISIBILITY_DESCRIPTION}`}>
                    <div className="flex items-center gap-2">
                      <div className={OVERVIEW.visBarTrack}><div className={OVERVIEW.visBarFill} style={{ width: `${a.visibility.score}%` }} /></div>
                      <span className={OVERVIEW.visOf}>{a.visibility.searches} of {a.visibility.of}</span>
                      <span className={OVERVIEW.visScore}>{a.visibility.score}%</span>
                    </div>
                  </td>
                  {channels.map(c => <ChannelCells key={c} channel={c} value={a.channels[c]} />)}
                  {showDevices && <td className={OVERVIEW.tdCenter}>{a.devices.mobile.searches}</td>}
                  {showDevices && <td className={OVERVIEW.tdCenter}>{a.devices.desktop.searches}</td>}
                  <td className={`${OVERVIEW.tdCenter} cursor-help`} title={listTitle("Keywords", a.keywordList, keywordsSearched)}>{a.keywords} of {keywordsSearched}</td>
                  <td className={`${OVERVIEW.tdCenter} cursor-help`} title={listTitle("Postcodes", a.postcodeList, postcodesSearched)}>{a.postcodes} of {postcodesSearched}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className={OVERVIEW.panelFooter}>
        Seen = searches they appeared in · Avg pos = average position when seen (1 = top).
        {hiddenChannels.length > 0 && ` ${hiddenChannels.map(c => CHANNEL_NAMES[c]).join(", ")} column${hiddenChannels.length === 1 ? "" : "s"} hidden (none seen).`}
        {onOpenAdvertiser && " Click a row to open its Advertiser Snapshot."}
      </div>
      {menu}
    </div>
  );
}

// Hover text for the Keywords / Locations cells: "Keywords (3 of 5):" then one per line.
function listTitle(label, list, of) {
  return `${label} (${list.length} of ${of}):\n${list.join("\n")}`;
}

// Two cells per channel: searches seen in (as a coloured badge), then average position.
function ChannelCells({ channel, value }) {
  if (!value) {
    return (
      <>
        <td className={OVERVIEW.td}><span className={OVERVIEW.muted}>–</span></td>
        <td className={OVERVIEW.td}><span className={OVERVIEW.muted}>–</span></td>
      </>
    );
  }
  const title = `Seen in ${value.searches} searches` +
    (channel === "shopping" && value.withPrice ? `, shown with a price in ${value.withPrice}` : "");
  return (
    <>
      <td className={OVERVIEW.td}>
        <span className={`${OVERVIEW.badge} ${CHANNEL_BADGE[channel]}`} title={title}>{value.searches}</span>
      </td>
      <td className={`${OVERVIEW.td} text-slate-600`} title={value.avgPosition != null ? `Average position ${value.avgPosition}` : undefined}>
        {value.avgPosition != null ? value.avgPosition : <span className={OVERVIEW.muted}>–</span>}
      </td>
    </>
  );
}
